import { useMutation } from '@tanstack/react-query';
import { ExternalLink, LoaderCircle } from 'lucide-react';
import { toast } from 'sonner';
import { problemText, toApiError, useApi } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

/** Für diese Codes schreibt der Server bewusst einen deutschen Satz, der den Fall unterscheidet. */
const GERMAN_DETAIL = ['onboarding_guest_link_not_found', 'onboarding_guest_link_not_recoverable'];

interface Props {
  bookingId: string;
  propertyId: string;
  className?: string;
}

/**
 * Öffnet das Gastportal der Buchung in einem neuen Tab. Der Tab entsteht noch im Klick, sonst
 * blockt der Browser das Fenster; scheitert der Link, schließt er sich wieder und ein deutscher
 * Satz nennt den Grund – nie der englische Entwicklertext des Servers.
 */
export function OnboardingGuestLinkButton({ bookingId, propertyId, className }: Props) {
  const api = useApi();
  const openLink = useMutation({
    mutationFn: () => api.calendar.openOnboardingGuestLink(bookingId, propertyId),
  });

  const handleClick = async () => {
    const guestTab = window.open('about:blank', '_blank');
    if (guestTab) guestTab.opener = null;
    try {
      const result = await openLink.mutateAsync();
      if (guestTab) guestTab.location.replace(result.url);
      else window.location.assign(result.url);
    } catch (error) {
      guestTab?.close();
      toast.error(
        problemText(await toApiError(error), 'Onboarding-Link konnte nicht geöffnet werden.', {
          detailFor: GERMAN_DETAIL,
        }),
      );
    }
  };

  return (
    <button
      type="button"
      disabled={openLink.isPending}
      onClick={() => void handleClick()}
      className={cn(
        className ??
          'border-line hover:border-sage flex min-h-11 w-full items-center rounded-xl border px-4 text-sm font-semibold',
        'justify-between gap-2 disabled:opacity-60',
      )}
    >
      Onboarding-Link öffnen
      {openLink.isPending ? (
        <LoaderCircle size={16} className="motion-safe:animate-spin" aria-hidden />
      ) : (
        <ExternalLink size={16} aria-hidden />
      )}
    </button>
  );
}
